const COOKIE_NAME = 'user-selected-country'
const COOKIE_DOMAIN = location.hostname.includes('myshopify.com')
  ? location.hostname
  : 'toteme.com'
const COOKIE_EXPIRES_DAYS = 30

class CountrySwitcher extends window.BAO.CustomElement() {
  constructor () {
    super()
    this.showOnLoad = false
    this.isRedirectEnabled = false
    this.currentCountry = ''
  }

  connectedCallback () {
    super.connectedCallback()

    this.showOnLoad = this.getBooleanAttr('data-show-on-load')
    this.isRedirectEnabled = this.getBooleanAttr('data-is-redirect-enabled')
    this.currentCountry = this.getAttribute('data-current-country') || ''
    this.currentStore = this.getAttribute('data-store') || ''

    this.init()
  }

  setupListeners () {
    super.setupListeners()

    this.openSwitcher = this.openSwitcher.bind(this)
    this.closeSwitcher = this.closeSwitcher.bind(this)

    this.listeners.add(
      this.els.submit.element,
      'click',
      this.handleSubmit.bind(this),
    )

    this.listeners.add(
      this.externalEls.trigger.element,
      'click',
      this.openSwitcher,
    )

    this.listeners.add(this.els.close.element, 'click', this.closeSwitcher)
  }

  async init () {
    this.handleUrlOverride()
    const cookie = this.handleCookie()

    // Early return if cookie instructs redirect away
    if (
      cookie &&
      (cookie.country !== this.currentCountry ||
        this.currentStore === 'toteme-studio-se.myshopify.com')
    ) {
      const redirectUrl = this.getRedirectUrlFromCookie(cookie)
      if (redirectUrl && this.isRedirectEnabled) {
        window.location.href = redirectUrl
        return
      }
    }

    // Compute selected country
    const detectedCountry = await this.detectCustomerCountry()
    const selectedCountry =
      cookie?.country || detectedCountry || this.currentCountry
    this.populateSelect(selectedCountry)

    if (this.showOnLoad) {
      await this.handleCountryCheckFlow()
    } else {
      this.closeSwitcher()
    }
  }

  get countries () {
    const raw = window.theme?.countries ?? {}
    return Object.keys(raw).map((code) => {
      const entry = raw[code] ?? {}
      return {
        code,
        name: entry.name || code,
        currency: entry.currency || '',
        url: entry.url || entry.site || '',
      }
    })
  }

  getBooleanAttr (attrName) {
    const val = this.getAttribute(attrName)
    if (val === null) return false
    return val === 'true' || val === '1'
  }

  setCookie (name, value, days = COOKIE_EXPIRES_DAYS) {
    const expires = new Date()
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
    const cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=/; expires=${expires.toUTCString()}; domain=${COOKIE_DOMAIN}`
    document.cookie = cookie
  }

  getCookie (name) {
    const matches = document.cookie.match(
      new RegExp(
        `(?:^|; )${name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1')}=([^;]*)`,
      ),
    )
    return matches ? decodeURIComponent(matches[1]) : null
  }

  getSite (countryCode) {
    return this.countries.find((s) => s.code === countryCode)
  }

  getCountryUrl (countryCode) {
    const site = this.getSite(countryCode)
    return site ? site.url : null
  }

  getRedirectUrl (siteObj) {
    try {
      const url = new URL(siteObj.url)
      url.pathname = location.pathname
      url.search = location.search
      url.hash = location.hash
      url.searchParams.set('country', siteObj.code)
      url.searchParams.set('curr', siteObj.currency)
      url.searchParams.set('loc', siteObj.code)
      url.searchParams.set('name', siteObj.name)
      url.searchParams.set('currency', siteObj.currency)
      return url.href
    } catch (err) {
      return null
    }
  }

  async detectCustomerCountry () {
    try {
      const res = await fetch('/browsing_context_suggestions.json', {
        credentials: 'same-origin',
      })
      if (!res.ok) throw new Error('Failed fetching suggestions')
      const data = await res.json()
      return (
        data?.detected_values?.country?.handle || this.currentCountry || null
      )
    } catch (err) {
      return this.currentCountry || null
    }
  }

  populateSelect (selectedCode) {
    const options = this.countries
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((site) => {
        const selected =
          selectedCode && selectedCode === site.code ? ' selected' : ''
        const safeName = site.name.replace(/"/g, '&quot;')
        return `<option value="${site.code}" data-country="${site.code}" data-currency="${site.currency}" data-name="${safeName}" ${selected}>${safeName}</option>`
      })
      .join('')
    this.els.select.element.innerHTML = options
  }

  handleUrlOverride () {
    const searchParams = new URLSearchParams(location.search)
    if (!searchParams.has('user-selected-country')) return
    const countryCode = searchParams.get('user-selected-country')
    const site = this.getSite(countryCode)
    if (!site) return
    const cookie = JSON.stringify({
      country: site.code,
      name: site.name,
      currency: site.currency,
    })
    this.setCookie(COOKIE_NAME, cookie, COOKIE_EXPIRES_DAYS)
  }

  handleCookie () {
    const c = this.getCookie(COOKIE_NAME)
    if (!c) return null
    try {
      return JSON.parse(c)
    } catch (err) {
      return null
    }
  }

  handleGtm (country, name, currency, countryUrl) {
    const dataLayer = window.dataLayer || []
    dataLayer.push({
      event: 'country_switcher_submit',
      countrySwitcherSubmit: {
        country_code: country,
        name,
        currency,
        country_url: countryUrl,
      },
    })
  }

  handleSubmit () {
    const selectedOption =
      this.els.select.element.options[this.els.select.element.selectedIndex]

    if (!selectedOption) return
    const country = selectedOption.value
    const name = selectedOption.dataset.name || ''
    const currency = selectedOption.dataset.currency || ''
    const countryUrl = this.getCountryUrl(country) || ''
    const cookie = JSON.stringify({ country, name, currency })
    this.setCookie(COOKIE_NAME, cookie, COOKIE_EXPIRES_DAYS)
    if (this.currentCountry === country) {
      this.closeSwitcher()
      return
    }
    const redirectUrl = this.getRedirectUrl({
      code: country,
      name,
      currency,
      url: countryUrl,
    })

    this.handleGtm(country, name, currency, countryUrl)
    if (redirectUrl && this.isRedirectEnabled) {
      window.location.href = redirectUrl
    } else {
      this.closeSwitcher()
    }
  }

  getRedirectUrlFromCookie (cookieObj) {
    if (!cookieObj) return null
    const site = this.getSite(cookieObj.country)
    if (!site) return null
    return this.getRedirectUrl({
      code: cookieObj.country,
      currency: cookieObj.currency,
      name: cookieObj.name,
      url: site.url,
    })
  }

  async handleRedirectIfGeoTag (customerCountry) {
    const params = new URLSearchParams(location.search)
    if (params.get('geo') === '1' && customerCountry) {
      const site = this.getSite(customerCountry)
      if (site) {
        const redirectUrl = this.getRedirectUrl({
          code: customerCountry,
          currency: site.currency,
          name: site.name,
          url: site.url,
        })
        if (redirectUrl && this.isRedirectEnabled) {
          window.location.href = redirectUrl
        }
      }
    }
  }

  async handleCountryCheckFlow () {
    if (window.Shopify && window.Shopify.designMode) return

    const cookie = this.handleCookie()
    // Early return if cookie matches current store country
    if (cookie && cookie.country === this.currentCountry) return

    // Redirect if cookie differs
    if (cookie && cookie.country && cookie.country !== this.currentCountry) {
      const redirectUrl = this.getRedirectUrlFromCookie(cookie)
      if (redirectUrl && this.isRedirectEnabled) {
        window.location.href = redirectUrl
        return
      }
    }

    const customerCountry = await this.detectCustomerCountry()
    await this.handleRedirectIfGeoTag(customerCountry)

    // If no cookie and detected country differs from store
    if (!cookie && customerCountry && customerCountry !== this.currentCountry) {
      const site = this.getSite(customerCountry)
      if (!site) {
        this.openSwitcher()
        return
      }
      const redirectUrl = this.getRedirectUrl({
        code: customerCountry,
        currency: site.currency,
        name: site.name,
        url: site.url,
      })
      if (redirectUrl && this.isRedirectEnabled) {
        window.location.href = redirectUrl
        return
      }
      this.openSwitcher()
      return
    }

    // Nothing to redirect. Only open switcher if showOnLoad true
    if (this.showOnLoad) this.openSwitcher()
  }

  openSwitcher () {
    this.classList.toggle('drw-CountrySwitcher-active', true)
  }

  closeSwitcher () {
    this.classList.toggle('drw-CountrySwitcher-active', false)
  }
}

if (!customElements.get('country-switcher')) {
  customElements.define('country-switcher', CountrySwitcher)
}
